import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import UpgradeRequestDialog from '../components/UpgradeRequestDialog';

const SavedSearches = () => {
  const { isAuthenticated, token, tierInfo } = useAuth();
  const navigate = useNavigate();
  const [savedSearches, setSavedSearches] = useState([]);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [searchToDelete, setSearchToDelete] = useState(null);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // API base URL - use environment variable or fallback to localhost
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const fetchSavedSearches = useCallback(async () => {
    // Don't fetch if user can't save searches
    if (tierInfo && !tierInfo.can_save_searches) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/saved-searches`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch saved searches');
      }

      const data = await response.json();
      setSavedSearches(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, token, tierInfo]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSavedSearches();
    }
  }, [isAuthenticated, fetchSavedSearches]);

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  const handleLoadSearch = (searchParams) => {
    // Extract state from saved search params to determine route
    const savedParams = searchParams.search_params_json;
    const state = savedParams?.state || 'Texas'; // Default to Texas if not specified
    const datasetRoute = state.toLowerCase(); // 'Texas' -> 'texas'
    
    // Navigate to dataset-specific search page with the saved search parameters
    navigate(`/app/search/${datasetRoute}`, { 
      state: { 
        loadSavedSearch: true, 
        searchParams: savedParams
      }
    });
  };

  const handleDeleteSearch = (search) => {
    // Show confirmation dialog first
    setSearchToDelete(search);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteSearch = async () => {
    if (!searchToDelete) return;
    
    try {
      setDeleteLoading(prev => ({ ...prev, [searchToDelete.id]: true }));
      
      const response = await fetch(`${API_BASE_URL}/saved-searches/${searchToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete search');
      }

      // Remove from local state
      setSavedSearches(prev => prev.filter(search => search.id !== searchToDelete.id));
      
      // Close dialog and reset state
      setDeleteDialogOpen(false);
      setSearchToDelete(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleteLoading(prev => ({ ...prev, [searchToDelete.id]: false }));
    }
  };

  const cancelDeleteSearch = () => {
    setDeleteDialogOpen(false);
    setSearchToDelete(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (e) {
      return 'Invalid date';
    }
  };

  const getTablesFromSearchParams = (searchParamsJson) => {
    if (!searchParamsJson) return 'None';
    
    try {
      // Parse if it's a string
      const params = typeof searchParamsJson === 'string' 
        ? JSON.parse(searchParamsJson) 
        : searchParamsJson;
      
      const tableNames = [];
      
      // Map boolean flags to display names
      if (params.search_wells) tableNames.push('Wells');
      if (params.search_production) tableNames.push('Production');
      if (params.search_inspections) tableNames.push('Inspections');
      if (params.search_violations) tableNames.push('Violations');
      if (params.search_severances) tableNames.push('Severances');
      if (params.search_spills) tableNames.push('Spills');
      
      return tableNames.length > 0 ? tableNames.join(', ') : 'None';
    } catch (e) {
      return 'Unknown';
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedSearches = [...savedSearches].sort((a, b) => {
    let aVal, bVal;
    
    switch (sortField) {
      case 'search_name':
        aVal = a.search_name?.toLowerCase() || '';
        bVal = b.search_name?.toLowerCase() || '';
        break;
      case 'created_at':
        aVal = new Date(a.created_at);
        bVal = new Date(b.created_at);
        break;
      default:
        return 0;
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading saved searches...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>
      <Box>
        <Typography
          component="h1"
          sx={{ fontWeight: 750, fontSize: { xs: '2rem', md: '2.4rem' }, letterSpacing: '-0.03em', mb: 4 }}
        >
          Saved searches
        </Typography>

        {/* Tier Check - Show upgrade message for Basic users */}
        {tierInfo && !tierInfo.can_save_searches && (
          <Paper elevation={0} sx={{ 
            p: 2, 
            mb: 3,
            width: '100%',
            overflow: 'hidden',
            textAlign: 'center',
            border: 1,
            borderColor: 'divider'
          }}>
            <SaveIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" color="primary.main" gutterBottom>
              Save Searches Feature
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Save searches to quickly access your frequently used queries. This feature is available with Pro tier.
            </Typography>
            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
              💡 <button 
                onClick={() => setUpgradeDialogOpen(true)}
                style={{ 
                  color: 'inherit', 
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                Upgrade to Pro to save and manage your searches
              </button>
            </Typography>
          </Paper>
        )}

        {/* Only show saved searches content if user can save searches */}
        {tierInfo && tierInfo.can_save_searches && (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {savedSearches.length === 0 ? (
          <Paper elevation={0} sx={{ p: 2, textAlign: 'center', border: 1, borderColor: 'divider' }}>
            <SaveIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No saved searches yet
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Save your first search after running a query to see it here!
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/app')}
              startIcon={<SearchIcon />}
            >
              Start Your First Search
            </Button>
          </Paper>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" color="text.secondary">
                {savedSearches.length} saved search{savedSearches.length !== 1 ? 'es' : ''}
              </Typography>
              <Button
                variant="outlined"
                onClick={fetchSavedSearches}
                startIcon={<RefreshIcon />}
              >
                Refresh
              </Button>
            </Box>

                         <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 600, border: 1, borderColor: 'divider' }}>
               <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          fontWeight: 'bold', 
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'grey.100' }
                        }}
                        onClick={() => handleSort('search_name')}
                      >
                        Name
                        {sortField === 'search_name' && (
                          <Box component="span" sx={{ ml: 1 }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 'bold', 
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'grey.100' }
                        }}
                        onClick={() => handleSort('created_at')}
                      >
                        Created
                        {sortField === 'created_at' && (
                          <Box component="span" sx={{ ml: 1 }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Dataset(s)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                 <TableBody>
                   {sortedSearches.map((search, index) => (
                     <TableRow 
                       key={search.id}
                       sx={{
                         '&:last-child td': { borderBottom: 'none' }
                       }}
                     >
                       <TableCell>
                         <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                           {search.search_name}
                        </Typography>
                      </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(search.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {getTablesFromSearchParams(search.search_params_json)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Tooltip title="Load Saved Search">
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleLoadSearch(search)}
                              >
                                <SearchIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Saved Search">
                              <span>
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleDeleteSearch(search)}
                                disabled={deleteLoading[search.id]}
                              >
                                {deleteLoading[search.id] ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <DeleteIcon />
                                )}
                              </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                     </TableRow>
                   ))}
                 </TableBody>
               </Table>
             </TableContainer>
          </>
        )}
          </>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDeleteSearch}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the saved search "{searchToDelete?.search_name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteSearch} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDeleteSearch} color="error" variant="contained" disabled={deleteLoading[searchToDelete?.id]}>
            {deleteLoading[searchToDelete?.id] ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Upgrade Request Dialog */}
      <UpgradeRequestDialog 
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
      />
    </Container>
  );
};

export default SavedSearches;
